/** Property hygiene: key casing, bucketing, and the content policy. */

const snakeCase = (str: string): string =>
  str
    .replace(/([A-Z])/g, "_$1")
    .toLowerCase()
    .replace(/^_/, "");

export function snakeCaseKeys(
  properties: Record<string, unknown>,
): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(properties)) {
    out[snakeCase(key)] = value;
  }
  return out;
}

export function bucketCharCount(count: number): string {
  if (count <= 0) return "0";
  if (count <= 20) return "1-20";
  if (count <= 100) return "21-100";
  if (count <= 500) return "101-500";
  if (count <= 2000) return "501-2000";
  return "2000+";
}

export function bucketCount(count: number): string {
  if (count <= 0) return "0";
  if (count === 1) return "1";
  if (count <= 5) return "2-5";
  if (count <= 20) return "6-20";
  return "20+";
}

export function bucketByteSize(bytes: number): string {
  if (bytes <= 0) return "0";
  if (bytes < 10_000) return "<10kb";
  if (bytes < 100_000) return "10kb-100kb";
  if (bytes < 1_000_000) return "100kb-1mb";
  if (bytes < 10_000_000) return "1mb-10mb";
  if (bytes < 100_000_000) return "10mb-100mb";
  return "100mb+";
}

export function extensionOf(path: string | undefined | null): string {
  if (!path) return "";
  const base = path.split("/").pop() ?? "";
  const dot = base.lastIndexOf(".");
  if (dot <= 0) return "";
  return base.slice(dot + 1).toLowerCase();
}

export function sameEmailDomain(
  a: string | undefined | null,
  b: string | undefined | null,
): boolean {
  const domainOf = (email: string | undefined | null) =>
    email?.includes("@") ? email.split("@").pop()!.toLowerCase() : null;
  const da = domainOf(a);
  const db = domainOf(b);
  return !!da && da === db;
}

/**
 * Content policy: no message text, no emails, no whole API objects on any
 * event. The two sanctioned exceptions carry an allowlist entry.
 */
const FORBIDDEN_KEYS = new Set([
  "content",
  "message_content",
  "email",
  "target_user_email",
  "instructions",
  "query",
]);

const PER_EVENT_ALLOWLIST: Record<string, string[]> = {
  "Search Performed": ["query"],
  "Message Feedback Submitted": ["feedback", "message_content"],
};

export function assertNoForbiddenKeys(
  event: string,
  properties: Record<string, unknown>,
): void {
  const allowed = PER_EVENT_ALLOWLIST[event] ?? [];
  for (const key of Object.keys(properties)) {
    if (FORBIDDEN_KEYS.has(key) && !allowed.includes(key)) {
      throw new Error(
        `analytics: forbidden property "${key}" on event "${event}"`,
      );
    }
  }
}
